import Checkbox from "@mui/joy/Checkbox";
import Sheet from "@mui/joy/Sheet";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { createClient } from "@supabase/supabase-js";
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Snowfall from "../holidays/Snowfall";
import { a11yProps, CustomTabPanel } from "../tabs/CustomTabPanel";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "url";
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY || "xyz";
const supabase = createClient(supabaseUrl, supabaseKey);

const TRIVIA_YEARS = [2024, 2025];

export default function Dashboard() {
  const [value, setValue] = React.useState(1); // Default to 2025

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          {TRIVIA_YEARS.map((year, index) => (
            <Tab key={year} label={year.toString()} {...a11yProps(index)} />
          ))}
        </Tabs>
      </Box>
      {TRIVIA_YEARS.map((year, index) => (
        <CustomTabPanel key={year} value={value} index={index}>
          <YearlyDashboard year={year} />
        </CustomTabPanel>
      ))}
    </Box>
  );
}

interface TriviaScoresGraphProps {
  scores: { score: number }[];
}

function TriviaScoresGraph({ scores }: TriviaScoresGraphProps) {
  const [graphData, setGraphData] = useState<
    { numberOfCorrectGuesses: number; count: number }[]
  >([]);

  const MAX_POSSIBLE_SCORE = 10;

  useEffect(() => {
    const graphData: { numberOfCorrectGuesses: number; count: number }[] = [];
    for (let i = 0; i <= MAX_POSSIBLE_SCORE; i++) {
      graphData.push({ numberOfCorrectGuesses: i, count: 0 });
    }

    scores.forEach(({ score }) => {
      graphData[score].count++;
    });

    setGraphData(graphData);
  }, [scores]);

  return (
    <div className="h-60 flex flex-col items-center gap-4 border-solid border-2 rounded-md border-indigo-600">
      <h1 className="text-xl font-bold">
        Score Distribution [n={scores.length}]
      </h1>

      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={graphData}
          margin={{
            left: -20,
            right: 20,
          }}
        >
          <XAxis dataKey="numberOfCorrectGuesses" />
          <YAxis />
          <Tooltip />
          <Bar
            dataKey="count"
            fill="#8884d8"
            activeBar={<Rectangle fill="pink" stroke="blue" />}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface TriviaAnswersGraphProps {
  answers: { question_id: string; guess: string; is_correct: boolean }[];
  year: number;
}

function TriviaAnswersGraph({ answers, year }: TriviaAnswersGraphProps) {
  const [showCorrectness, setShowCorrectness] = useState(true);
  const [showAmigos, setShowAmigos] = useState(false);
  const [graphData, setGraphData] = useState<
    {
      quetionId: number;
      correctCount: number;
      myronCount: number;
      maxCount: number;
      liamCount: number;
      jaceCount: number;
      glennCount: number;
      joshCount: number;
    }[]
  >([]);

  useEffect(() => {
    const graphDataMapping: {
      [key: number]: {
        quetionId: number;
        correctCount: number;
        incorrectCount: number;
        myronCount: number;
        maxCount: number;
        liamCount: number;
        jaceCount: number;
        glennCount: number;
        joshCount: number;
      };
    } = {};

    answers.forEach((answer) => {
      const questionId = parseInt(answer.question_id) - 1; // Assumes that the questionId has Int form.

      if (!graphDataMapping[questionId]) {
        graphDataMapping[questionId] = {
          quetionId: questionId,
          correctCount: 0,
          incorrectCount: 0,
          myronCount: 0,
          maxCount: 0,
          liamCount: 0,
          jaceCount: 0,
          glennCount: 0,
          joshCount: 0,
        };
      }

      if (answer.is_correct) {
        graphDataMapping[questionId].correctCount++;
      } else {
        graphDataMapping[questionId].incorrectCount++;
      }

      if (answer.guess === "Myron") {
        graphDataMapping[questionId].myronCount++;
      }

      if (answer.guess === "Max") {
        graphDataMapping[questionId].maxCount++;
      }

      if (answer.guess === "Liam") {
        graphDataMapping[questionId].liamCount++;
      }

      if (answer.guess === "Jace") {
        graphDataMapping[questionId].jaceCount++;
      }

      if (answer.guess === "Glenn") {
        graphDataMapping[questionId].glennCount++;
      }

      if (answer.guess === "Josh") {
        graphDataMapping[questionId].joshCount++;
      }
    });

    const unsortedGraphData = [];
    for (let key in graphDataMapping) {
      unsortedGraphData.push(graphDataMapping[key]);
    }

    const sortedGraphData = [...unsortedGraphData].sort(
      (a, b) => a.quetionId - b.quetionId
    );

    setGraphData(sortedGraphData);
  }, [answers]);

  return (
    <div className="flex flex-col items-center gap-4 border-solid border-2 rounded-md border-indigo-600">
      <h1 className="text-xl font-bold">Answer Details</h1>

      <div className="flex w-full h-60">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={graphData}
            margin={{
              left: -20,
              right: 20,
            }}
          >
            {/* To get ticks to show up, try using the `ticks` parameter */}
            <XAxis dataKey="questionId" />
            <YAxis />
            <Tooltip />

            {showCorrectness && (
              <Bar
                dataKey="correctCount"
                fill="#00cc00"
                activeBar={<Rectangle fill="pink" stroke="blue" />}
              />
            )}

            {showCorrectness && (
              <Bar
                dataKey="incorrectCount"
                fill="#ff0000"
                activeBar={<Rectangle fill="pink" stroke="blue" />}
              />
            )}

            {showAmigos && (
              <Bar
                dataKey="myronCount"
                fill="#00ccff"
                activeBar={<Rectangle fill="gold" stroke="purple" />}
              />
            )}

            {showAmigos && (
              <Bar
                dataKey="maxCount"
                fill="#0099ff"
                activeBar={<Rectangle fill="gold" stroke="purple" />}
              />
            )}

            {showAmigos && (
              <Bar
                dataKey="liamCount"
                fill="#0066ff"
                activeBar={<Rectangle fill="gold" stroke="purple" />}
              />
            )}

            {showAmigos && (
              <Bar
                dataKey="jaceCount"
                fill="#3366ff"
                activeBar={<Rectangle fill="gold" stroke="purple" />}
              />
            )}

            {showAmigos && year === 2024 && (
              <Bar
                dataKey="glennCount"
                fill="#3333cc"
                activeBar={<Rectangle fill="gold" stroke="purple" />}
              />
            )}

            {showAmigos && year === 2025 && (
              <Bar
                dataKey="joshCount"
                fill="#6600cc"
                activeBar={<Rectangle fill="gold" stroke="purple" />}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-row gap-4 w-full items-center justify-center pb-4">
        <Sheet className="p-2 flex rounded-md" variant="outlined">
          <Checkbox
            overlay
            label="Show Correctness"
            defaultChecked
            onChange={(e) => {
              setShowCorrectness(e.target.checked);
            }}
          />
        </Sheet>
        <Sheet className="p-2 flex rounded-md" variant="outlined">
          <Checkbox
            label="Show Amigos"
            overlay
            onChange={(e) => {
              setShowAmigos(e.target.checked);
            }}
          />
        </Sheet>
      </div>
    </div>
  );
}

interface YearlyDashboardProps {
  year: number;
}

export function YearlyDashboard({ year }: YearlyDashboardProps) {
  const [allTriviaScores, setAllTriviaScores] = useState<{ score: number }[]>(
    []
  );
  const [allTriviaAnswers, setAllTriviaAnswers] = useState<
    { question_id: string; guess: string; is_correct: boolean }[]
  >([]);

  useEffect(() => {
    const updateAllTriviaScores = async () => {
      const { data } = await supabase
        .from("triviaScores")
        .select("score")
        .eq("year", year);

      setAllTriviaScores(data ?? []);
    };

    updateAllTriviaScores();
  }, [year]);

  useEffect(() => {
    const updateAllTriviaAnswers = async () => {
      const { data } = await supabase
        .from("triviaAnswers")
        .select("question_id, guess, is_correct")
        .eq("year", year);

      setAllTriviaAnswers(data ?? []);
    };

    updateAllTriviaAnswers();
  }, [year]);

  return (
    <div className="h-full">
      <Snowfall />
      <div className="flex flex-col gap-4 m-2">
        <TriviaScoresGraph scores={allTriviaScores} />
        <TriviaAnswersGraph answers={allTriviaAnswers} year={year} />
      </div>
    </div>
  );
}
